import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import type { ArtStyleSummary } from '../../art-style-api';
import { artStyleLabel } from '../../useActiveArtStyle';
import { usePortalMenu, type PortalMenuAlign } from '../../usePortalMenu';
import {
  Check,
  MenuPanel,
  Option,
  OptionCopy,
  OptionHint,
  OptionLabel,
  PillLabel,
  PillName,
  StylePill,
  Thumb,
} from './ArtStylePicker.styles';

export type ArtStylePickerProps = {
  /** The styles a draw can use (each with an extracted definition), the active one first. */
  styles: ArtStyleSummary[];
  /** The picked style's id; '' (or an id not listed) for no style. */
  value: string;
  onChange: (styleId: string) => void;
  /** A run is in flight: the pill stays, greyed. */
  disabled?: boolean;
  /** Which edge of the pill the list lines up with (`start` by default). */
  align?: PortalMenuAlign;
  /** Offer "No style" first (a brand without a primary style draws with none unless it picks one). */
  allowNone?: boolean;
  className?: string;
};

/**
 * Which of the brand's art styles a draw uses, on a generate sheet in place of the read-only "Art style" chip: the
 * primary (active) one by default, or any other with a definition, drafts included (labelled). Hidden while the
 * brand has none. Controlled: the sheet keeps the pick and sends it as `style_id` when it is not the default.
 */
export const ArtStylePicker = ({
  styles,
  value,
  onChange,
  disabled = false,
  align = 'start',
  allowNone = false,
  className,
}: ArtStylePickerProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const menu = usePortalMenu<HTMLButtonElement>({ align });
  if (styles.length === 0) return null;

  const picked = styles.find((style) => style.id === value);
  const nameOf = (style: ArtStyleSummary): string => artStyleLabel(style.name) || t('Untitled style');

  const choose = (next: string) => (): void => {
    menu.close(true);
    if (next !== value) onChange(next);
  };

  return (
    <>
      <StylePill
        ref={menu.triggerRef}
        type="button"
        className={className}
        $open={menu.open}
        aria-haspopup="menu"
        aria-expanded={menu.open}
        aria-label={t('Art style: {{name}}', { name: picked ? nameOf(picked) : t('No style') })}
        title={t('Which of your art styles this draw follows. Manage them on the Art Style tab.')}
        disabled={disabled}
        onClick={() => {
          if (!disabled) menu.setOpen(!menu.open);
        }}
      >
        <Icon name="palette" size={13} />
        <PillLabel>
          {t('Art style:')} <PillName>{picked ? nameOf(picked) : t('No style')}</PillName>
          {!picked || picked.status === 'active' ? null : ` · ${t('Draft')}`}
        </PillLabel>
        <Icon name="chevron-down" size={12} />
      </StylePill>
      {menu.open
        ? createPortal(
            <MenuPanel
              ref={menu.panelRef}
              role="menu"
              aria-label={t('Art style')}
              $shown={menu.place !== null}
              style={menu.panelStyle}
              onKeyDown={menu.onItemKeyDown}
            >
              {allowNone ? (
                <Option
                  type="button"
                  role="menuitemradio"
                  aria-checked={!picked}
                  $destructive={false}
                  onClick={choose('')}
                >
                  <Thumb aria-hidden="true">
                    <Icon name="palette" size={14} />
                  </Thumb>
                  <OptionCopy>
                    <OptionLabel text="No style" tag="span" />
                    <OptionHint text="Draw without an art style" tag="span" />
                  </OptionCopy>
                  <Check $on={!picked} aria-hidden="true">
                    <Icon name="check" size={14} />
                  </Check>
                </Option>
              ) : null}
              {styles.map((style) => (
                <Option
                  key={style.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={picked?.id === style.id}
                  $destructive={false}
                  onClick={choose(style.id)}
                >
                  <Thumb aria-hidden="true">
                    {style.thumb_url ? (
                      <img src={style.thumb_url} alt="" loading="lazy" decoding="async" draggable={false} />
                    ) : (
                      <Icon name="palette" size={14} />
                    )}
                  </Thumb>
                  <OptionCopy>
                    <OptionLabel raw text={nameOf(style)} tag="span" />
                    {style.status === 'active' ? (
                      <OptionHint text="Primary" tag="span" />
                    ) : (
                      <OptionHint text="Draft" tag="span" />
                    )}
                  </OptionCopy>
                  <Check $on={picked?.id === style.id} aria-hidden="true">
                    <Icon name="check" size={14} />
                  </Check>
                </Option>
              ))}
            </MenuPanel>,
            document.body,
          )
        : null}
    </>
  );
};

export default ArtStylePicker;
